import React, {useState} from 'react';
import {FiLogIn} from 'react-icons/fi';
import {IonContent, IonPage} from '@ionic/react';
import {useRecoilValue} from 'recoil';
import {Keyboard, KeyboardResize} from '@capacitor/keyboard';
import {Capacitor} from '@capacitor/core';
import {currentUserData} from '../state/currentUser/currentUser';
import actions from '../api/currentUser';
import {authService} from '../api/authService';
import useFetchCurrentUser from '../hooks/useFetchCurrentUser';
import UIHeading from '../components/UICommon/UIHeading';
import UIStackedInput from '../components/UICommon/UIStackedInput';
import UIButton from '../components/UICommon/UIButton';
import UILoadingIndicator from '../components/UICommon/UILoadingIndicator';
import UIErrorIndicator from '../components/UICommon/UIErrorIndicator';
import UIBackButton from '../components/UICommon/UIBackButton';

const SettingsPage: React.FC = () => {
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [studio, setStudio] = useState<string>('');
  const [inputsError, setInputsError] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [isUpdateError, setIsUpdateError] = useState(false);
  const [isUpdateSuccess, setIsUpdateSuccess] = useState(false);
  const [isKeyboard, setKeyboard] = useState(0);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const {isLoading, isError} = useFetchCurrentUser();
  const currentUser: any = useRecoilValue(currentUserData);
  const tmpStatus = 'Aktiv'; // todo - add actual status after adding payments

  if (Capacitor.isNativePlatform()) {
    Keyboard.setResizeMode({
      mode: KeyboardResize.None,
    });
    Keyboard.addListener('keyboardWillShow', (info) => {
      document?.activeElement?.scrollIntoView(true);
      setKeyboard(info.keyboardHeight);
      setIsKeyboardOpen(true);
    });

    Keyboard.addListener('keyboardWillHide', () => {
      setKeyboard(0);
      setIsKeyboardOpen(false);
    });
  }

  const updateData = () => {
    setIsUpdateLoading(true);
    if ((password == null && studio == null) || (password!.length == 0 && studio!.length == 0) ||
        password != confirmPassword) {
      setIsUpdateLoading(false);
      return setInputsError(true);
    } else if (password!.length > 0 && studio!.length > 0) {
      if (password.startsWith(' ') || studio.startsWith(' ')) {
        setIsUpdateLoading(false);
        return setInputsError(true);
      } else {
        setIsUpdateLoading(false);
        return actions.updateCurrentUser(
          studio as string,
          password as string,
        ).then(() => {
          setIsUpdateSuccess(true);
        });
      }
    } else if (password!.length > 0 && studio!.length == 0) {
      if (password.startsWith(' ')) {
        setIsUpdateLoading(false);
        return setInputsError(true);
      } else {
        setIsUpdateLoading(false);
        return actions.updateCurrentPassword(
          password as string,
        ).then(() => {
          setIsUpdateSuccess(true);
        });
      }
    } else if (password!.length == 0 && studio!.length > 0) {
      if (studio.startsWith(' ')) {
        setIsUpdateLoading(false);
        return setInputsError(true);
      } else {
        setIsUpdateLoading(false);
        return actions.updateCurrentStudio(
          studio as string,
        ).then(() => {
          setIsUpdateSuccess(true);
        });
      }
    } else {
      setIsUpdateLoading(false);
      return setIsUpdateError(true);
    }
  };

  const cancelSubscription = () => {
    // todo add cancelling subscription after adding payments
  };

  return (
    <IonPage className='bg-white px-6 pt-6 overflow-y-hidden'>
      <IonContent scrollY={isKeyboardOpen}>
        <UIHeading title='Inställningar' description='Hantera och ställ in dina önskade inställningar.'
          style='hidden md:block' longer={true} />
        <UIBackButton style='md:hidden' title='Inställningar' href='/home'/>
        <div className='font-Outfit text-center md:hidden font-normal text-navy-primary'>
          Hantera och ställ in dina önskade inställningar.
        </div>
        <hr className='mt-5'/>
        <button
          onClick={() => authService.logout()}
          className='md:flex justify-center items-center py-5 rounded-md text-red-700
          absolute right-0 top-24 md:top-12 hidden'>
          <div className='flex justify-center items-center mr-2'>
            <FiLogIn />
          </div>
          <label className='text-sm'>Logga ut</label>
        </button>
        { isLoading || isUpdateLoading ? <UILoadingIndicator/> : isError || isUpdateError ? <UIErrorIndicator/> :
        <div className='flex flex-col justify-between pb-12 pt-6 md:pt-16 h-5/6 md:h-3/4'>
          <form className='space-y-4'>
            <div className='pb-2 md:pb-5'>
              <UIStackedInput
                type='password'
                text={password!}
                setText={setPassword}
                labelText='Nytt lösenord'
                autoComplete='new-password'
                style='md:w-96'
                textStyle='font-medium text-base'
              />
              <UIStackedInput
                type='password'
                text={confirmPassword!}
                setText={setConfirmPassword}
                labelText='Bekräfta nytt lösenord'
                style='md:w-96'
                textStyle='font-medium text-base'
              />
            </div>
            <UIStackedInput
              text={studio!}
              setText={setStudio}
              placeholder={
                currentUser !== null && currentUser?.studio !== null && currentUser?.studio.length > 0 ?
                currentUser?.studio : ''}
              labelText='Studio'
              style='md:w-96'
              textStyle='font-medium text-base'
            />
          </form>
          {currentUser?.srt && <div className='flex flex-col font-Outfit'>
            <div className='font-medium border-b border-neutral-500'>Prenumeration</div>
            <div className='flex justify-between py-2'>
              <div className={`${tmpStatus.toLowerCase() !== 'aktiv' ?
                      'bg-pink-background border-pink-border text-pink-text' :
                      'bg-green-background text-green-text border-green-border'} border py-1 w-24 text-center ` +
                  'rounded'}>
                {tmpStatus}
              </div>
              <UIButton
                onClick={cancelSubscription}
                text='Avsluta prenumeration'
                type='outline'
                style='text-red-500 font-medium text-xs'/>
            </div>
          </div>}
          <div className='flex flex-col-reverse w-full items-center'>
            <UIButton type='full' text='Logga ut'
              style="font-semibold font-Manrope w-full round-10 md:w-1/4 mt-4 md:hidden"
              color="danger"
              onClick={() => authService.logout()}/>
            <UIButton type='full' text='Spara' style="font-semibold font-Manrope w-full round-10 md:w-1/4"
              onClick={updateData}/>
            {inputsError && <div className='text-red-600 font-semibold text-sm pt-3'>Ingenting har ändrats</div>}
            {isUpdateSuccess && (
              <div className='text-green-600 font-semibold text-sm pt-3'>
                Dina data har uppdaterats
              </div>)}
          </div>
        </div>
        }
        {isKeyboardOpen && <div style={{height: isKeyboard - 20}}></div>}
      </IonContent>
    </IonPage>
  );
};

export default SettingsPage;
